/*
 * withStore — registration-time HOC that wraps a React component with a Redux Provider.
 *
 * Used by Bootstrapper.js to inject the store before components are registered with the
 * vendored habitat module. Kept in its own file so it can be unit-tested independently
 * of the Bootstrapper's side-effectful initialization.
 *
 * For the sync path (habitat.js), the Bootstrapper calls `withStore(store, Component)` directly.
 * For the async path (habitat-async.js), the Bootstrapper calls `withStoreAsync(store, loader)`,
 * which returns a loader function that resolves to the wrapped component at mount time.
 */
import React from 'react'
import { Provider } from 'react-redux'

const withStore = (store, Component) => {
  const WithStore = props =>
    React.createElement(
      Provider,
      { store },
      React.createElement(Component, props)
    )
  WithStore.displayName = `withStore(${
    Component.displayName || Component.name || 'Component'
  })`
  return WithStore
}

// Wrap an async loader so the Provider is injected after the promise resolves.
// Input:  () => Promise<{ default: Component }>
// Output: () => Promise<{ default: WithStore(Component) }>
const withStoreAsync = (store, loader) => () =>
  loader().then(mod => {
    const Component = mod.default || mod
    return { default: withStore(store, Component) }
  })

export { withStore, withStoreAsync }
export default withStore
